import { isPlatformBrowser } from '@angular/common';
import {
    DOCUMENT,
    effect,
    inject,
    Injectable,
    PLATFORM_ID,
    Signal
} from '@angular/core';
import { THEME_STORAGE_KEY } from '../../constants/storage.constants';
import { DARK_THEME_CLASS, LIGHT_THEME_CLASS } from '../../constants/theme.constants';
import { createSignalSetting, SignalSetting } from '../../functions/signal-setting.function';

export type Theme = 'light' | 'dark';

/**
 * Narrows an arbitrary stored value to a valid {@link Theme}, so the primitive discards anything
 * else in favour of the (OS-derived) default.
 *
 * @param value - The raw value read back from storage.
 * @returns {boolean} True when the value is a recognised theme.
 */
export function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  // -----------------------------------------------------------------------------------------------------
  // @ DEPENDENCIES
  // -----------------------------------------------------------------------------------------------------

  private readonly document: Document = inject(DOCUMENT);

  private readonly platformId: object = inject(PLATFORM_ID);

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  private readonly window = isPlatformBrowser(this.platformId) ? window : null;

  private readonly setting: SignalSetting<Theme> = createSignalSetting<Theme>({
    key: THEME_STORAGE_KEY,
    defaultValue: () => this.systemPreference(),
    validate: isTheme,
  });

  // -----------------------------------------------------------------------------------------------------
  // @ COMPUTED VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly theme: Signal<Theme> = this.setting.value;

  // -----------------------------------------------------------------------------------------------------
  // @ EFFECTS
  // -----------------------------------------------------------------------------------------------------

  private readonly bodyClassEffect = effect(() => {
    this.setBodyClasses(this.theme());
  });

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor() {
    this.watchSystemPreference();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggles the theme between light and dark.
   *
   * @returns {void}
   */
  toggle(): void {
    this.setting.update((theme) => (theme === 'light' ? 'dark' : 'light'));
  }

  /**
   * Sets the theme to the specified value.
   *
   * @param theme - The new theme, either 'light' or 'dark'.
   * @returns {void}
   */
  set(theme: Theme): void {
    this.setting.set(theme);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Reads the OS colour-scheme preference, falling back to light when off-browser (SSR).
   *
   * @returns {Theme} The preferred theme derived from `prefers-color-scheme`.
   */
  private systemPreference(): Theme {
    return this.window?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  /**
   * Mirrors live OS colour-scheme changes into the theme. Registered in the constructor because a
   * root service never receives `ngOnInit` — that hook only fires on components and directives.
   *
   * @returns {void}
   */
  private watchSystemPreference(): void {
    this.window
      ?.matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (event) => this.setting.set(event.matches ? 'dark' : 'light'));
  }

  /**
   * Applies the theme classes to the document element.
   *
   * @param theme - The theme whose classes should be applied.
   * @returns {void}
   */
  private setBodyClasses(theme: Theme): void {
    const documentClassList = this.document.documentElement.classList;

    if (theme === 'dark') {
      documentClassList.add(DARK_THEME_CLASS);
      documentClassList.remove(LIGHT_THEME_CLASS);
    } else {
      documentClassList.add(LIGHT_THEME_CLASS);
      documentClassList.remove(DARK_THEME_CLASS);
    }
  }
}
